import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { getSucursalInfo } from "../services/auth";
import {
  addExtraToStudent,
  listExtraconfigs,
  listStudentExtraconfigs,
  removeExtraFromStudent,
} from "../services/payment";
import { useConfirm } from "../hook/useConfirm";

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : Object.values(value);
};

// Shared "Adicionar Extra" / "Remover Extra" modal, opened from the actions
// dropdown on ListaEstudantesLayer. `mode` picks which flow renders and
// which endpoint gets called; `record` is the registration row the action
// was triggered from (has `.id` = registrationId, `.studentId`, `.student`).
const StudentExtraModal = ({ modalId, mode, record, onDone }) => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const modalRef = useRef(null);
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sucursalId = getSucursalInfo()?.id;
  const studentId = record?.studentId || record?.student?.id;

  useEffect(() => {
    setSelectedId("");
    if (!record) {
      setOptions([]);
      return;
    }
    setLoading(true);

    if (mode === "add") {
      Promise.all([listExtraconfigs(sucursalId), listStudentExtraconfigs(sucursalId)])
        .then(([allResp, studentResp]) => {
          const assignedRecord = (studentResp.data || []).find((r) => r.studentId === studentId);
          const assignedIds = new Set(toArray(assignedRecord?.extraconfigs).map((ec) => String(ec.id)));
          setOptions((allResp.data || []).filter((ec) => !assignedIds.has(String(ec.id))));
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    } else {
      listStudentExtraconfigs(sucursalId)
        .then((resp) => {
          const assignedRecord = (resp.data || []).find((r) => r.studentId === studentId);
          setOptions(toArray(assignedRecord?.extraconfigs));
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }
  }, [record, mode, studentId, sucursalId]);

  const handleConfirm = async () => {
    if (!selectedId) {
      toast.error(t("estudante.extras.select"));
      return;
    }
    if (mode === "remove" && !(await confirm(t("estudante.extras.remove.confirm")))) return;

    setSubmitting(true);
    try {
      const payload = { registrationId: record.id, studentId, sucursalId, extraId: selectedId };
      if (mode === "add") {
        await addExtraToStudent(payload);
        toast.success(t("estudante.extras.add.success"));
      } else {
        await removeExtraFromStudent(payload);
        toast.success(t("estudante.extras.remove.success"));
      }
      onDone?.();
      Modal.getInstance(modalRef.current)?.hide();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("estudante.extras.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const isAdd = mode === "add";
  const title = isAdd ? t("estudante.extras.add.title") : t("estudante.extras.remove.title");
  const emptyMessage = isAdd ? t("estudante.extras.add.empty") : t("estudante.extras.remove.empty");

  return (
    <div className='modal fade' id={modalId} tabIndex={-1} aria-hidden='true' ref={modalRef}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content radius-16 bg-base'>
          <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
            <h6 className='mb-0'>{title}</h6>
            <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close' />
          </div>
          <div className='modal-body p-24'>
            <p className='text-secondary-light mb-16'>{record?.student?.name}</p>
            {loading ? (
              <p className='text-secondary-light mb-0'>{t("global.loading")}</p>
            ) : options.length === 0 ? (
              <p className='text-secondary-light mb-0'>{emptyMessage}</p>
            ) : (
              <select
                className='form-select'
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value=''>{t("estudante.extras.select")}</option>
                {options.map((ec) => (
                  <option key={ec.id} value={ec.id}>
                    {ec.name} — {ec.recuringAmount ?? ec.recurringAmount ?? 0}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
            <button type='button' className='btn btn-neutral-500 border-neutral-100 px-24' data-bs-dismiss='modal'>
              {t("global.cancel")}
            </button>
            <button
              type='button'
              className='btn btn-primary-600 px-24 d-flex align-items-center gap-2'
              onClick={handleConfirm}
              disabled={submitting || options.length === 0}
            >
              {submitting && <Icon icon='svg-spinners:ring-resize' />}
              {t("global.confirm")}
            </button>
          </div>
        </div>
      </div>
      {ConfirmModal}
    </div>
  );
};

export default StudentExtraModal;
